import { Link, useRouterState } from "@tanstack/react-router";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu, X, ChevronDown, ChevronRight, Code2, Smartphone, Server, Globe,
  Megaphone, ShoppingBag, Building2, LifeBuoy, Cpu, Palette, Sparkles,
  Search, Mail, BarChart3, Boxes, CreditCard, Cloud, HardDrive, Database,
  Users, Briefcase, BookOpen, MessageCircle, FileText, Network, ShieldCheck,
  Layers, Rocket, Zap, Target, PenTool, Layout, Smartphone as Mobile, Wrench,
  TrendingUp, Share2, Video, Megaphone as Mega, LineChart, Package, Truck,
  GraduationCap, Stethoscope, DollarSign, UserCheck, Building, MapPin, Award,
  Headphones, Brush, Send, Lock, Gauge, Bot, Store, Star, Phone, CheckCircle,
  ClipboardList, CalendarDays, Funnel, Landmark, AtSign,
} from "lucide-react";
import { Logo } from "./logo";
import { HeaderUtilities } from "./header-utilities";
import { cn } from "@/lib/utils";
import { useMegaMenu, type MegaMenuConfig } from "@/hooks/use-mega-menu";
import { resolveMegaMenuIcon } from "@/lib/mega-menu-icons";

type Item = { label: string; to: string; icon: React.ComponentType<{ className?: string }>; desc?: string };
type Group = { title: string; items: Item[] };
type Category = {
  label: string;
  desc: string;
  icon: React.ComponentType<{ className?: string }>;
  groups: Group[];
  to?: string;
};
type Menu = {
  label: string;
  categories: Category[];
  featured?: { title: string; desc: string; to: string };
  to?: string;
};

const menus: Menu[] = [
  {
    label: "Services",
    to: "/services",
    categories: [
      {
        label: "Web Development",
        desc: "Modern, fast, and scalable web applications.",
        icon: Code2,
        groups: [
          { title: "Stacks", items: [
            { label: "React & Next.js", to: "/services/web-development/react-nextjs", icon: Code2, desc: "Explore react & next.js" },
            { label: "MERN Stack", to: "/services/web-development/mern-stack", icon: Network, desc: "Explore mern stack" },
            { label: "Laravel / PHP", to: "/services/web-development/laravel-php", icon: Server, desc: "Explore laravel / php" },
            { label: "WordPress", to: "/services/web-development/wordpress", icon: Globe, desc: "Explore wordpress" },
          ]},
          { title: "Solutions", items: [
            { label: "SaaS Platforms", to: "/services/web-development/saas-platforms", icon: Boxes, desc: "Explore saas platforms" },
            { label: "Marketing Sites", to: "/services/web-development/marketing-sites", icon: Layout, desc: "Explore marketing sites" },
            { label: "Web Portals", to: "/services/web-development/web-portals", icon: Layers, desc: "Explore web portals" },
            { label: "Progressive Web Apps", to: "/services/web-development/progressive-web-apps", icon: Zap, desc: "Explore progressive web apps" },
          ]},
          { title: "Services", items: [
            { label: "API Integration", to: "/services/web-development/api-integration", icon: Cpu, desc: "Explore api integration" },
            { label: "Performance Audits", to: "/services/web-development/performance-audits", icon: Gauge, desc: "Explore performance audits" },
            { label: "Maintenance & Support", to: "/services/web-development/maintenance-support", icon: Wrench, desc: "Explore maintenance & support" },
          ]},
        ],
      },
      {
        label: "Mobile Application",
        desc: "Native-like mobile apps for iOS and Android.",
        icon: Smartphone,
        groups: [
          { title: "Platforms", items: [
            { label: "iOS (Swift)", to: "/services/mobile-application/ios-swift", icon: Mobile, desc: "Explore ios (swift)" },
            { label: "Android (Kotlin)", to: "/services/mobile-application/android-kotlin", icon: Mobile, desc: "Explore android (kotlin)" },
            { label: "React Native", to: "/services/mobile-application/react-native", icon: Code2, desc: "Explore react native" },
            { label: "Flutter", to: "/services/mobile-application/flutter", icon: Layers, desc: "Explore flutter" },
          ]},
          { title: "App Types", items: [
            { label: "On-demand Apps", to: "/services/mobile-application/on-demand-apps", icon: Rocket, desc: "Explore on-demand apps" },
            { label: "Marketplace Apps", to: "/services/mobile-application/marketplace-apps", icon: ShoppingBag, desc: "Explore marketplace apps" },
            { label: "Fintech Apps", to: "/services/mobile-application/fintech-apps", icon: DollarSign, desc: "Explore fintech apps" },
            { label: "Social Apps", to: "/services/mobile-application/social-apps", icon: Users, desc: "Explore social apps" },
          ]},
          { title: "Lifecycle", items: [
            { label: "App Strategy", to: "/services/mobile-application/app-strategy", icon: Target, desc: "Explore app strategy" },
            { label: "UI/UX & Prototyping", to: "/services/mobile-application/uiux-prototyping", icon: Palette, desc: "Explore ui/ux & prototyping" },
            { label: "Publishing & ASO", to: "/services/mobile-application/publishing-aso", icon: Award, desc: "Explore publishing & aso" },
          ]},
        ],
      },
      {
        label: "UI / UX Design",
        desc: "User-centric product design and experiences.",
        icon: Palette,
        groups: [
          { title: "Design", items: [
            { label: "Product Design", to: "/services/ui-ux-design/product-design", icon: PenTool, desc: "Explore product design" },
            { label: "Web Design", to: "/services/ui-ux-design/web-design", icon: Layout, desc: "Explore web design" },
            { label: "Mobile Design", to: "/services/ui-ux-design/mobile-design", icon: Mobile, desc: "Explore mobile design" },
            { label: "Design Systems", to: "/services/ui-ux-design/design-systems", icon: Layers, desc: "Explore design systems" },
          ]},
          { title: "Research", items: [
            { label: "User Research", to: "/services/ui-ux-design/user-research", icon: Users, desc: "Explore user research" },
            { label: "Usability Testing", to: "/services/ui-ux-design/usability-testing", icon: UserCheck, desc: "Explore usability testing" },
            { label: "Wireframing", to: "/services/ui-ux-design/wireframing", icon: Brush, desc: "Explore wireframing" },
          ]},
          { title: "Branding", items: [
            { label: "Brand Identity", to: "/services/ui-ux-design/brand-identity", icon: Sparkles, desc: "Explore brand identity" },
            { label: "Logo & Visual", to: "/services/ui-ux-design/logo-visual", icon: Award, desc: "Explore logo & visual" },
            { label: "Motion Graphics", to: "/services/ui-ux-design/motion-graphics", icon: Video, desc: "Explore motion graphics" },
          ]},
        ],
      },
      {
        label: "Tech Consultation",
        desc: "Expert guidance on architecture and strategy.",
        icon: Cpu,
        groups: [
          { title: "Strategy", items: [
            { label: "Product Discovery", to: "/services/tech-consultation/product-discovery", icon: Target, desc: "Explore product discovery" },
            { label: "MVP Planning", to: "/services/tech-consultation/mvp-planning", icon: Rocket, desc: "Explore mvp planning" },
            { label: "Tech Stack Audit", to: "/services/tech-consultation/tech-stack-audit", icon: Gauge, desc: "Explore tech stack audit" },
          ]},
          { title: "Architecture", items: [
            { label: "Cloud Architecture", to: "/services/tech-consultation/cloud-architecture", icon: Cloud, desc: "Explore cloud architecture" },
            { label: "Microservices", to: "/services/tech-consultation/microservices", icon: Network, desc: "Explore microservices" },
            { label: "DevOps & CI/CD", to: "/services/tech-consultation/devops-cicd", icon: Server, desc: "Explore devops & ci/cd" },
          ]},
          { title: "Security", items: [
            { label: "Security Audit", to: "/services/tech-consultation/security-audit", icon: ShieldCheck, desc: "Explore security audit" },
            { label: "Compliance (GDPR)", to: "/services/tech-consultation/compliance-gdpr", icon: Lock, desc: "Explore compliance (gdpr)" },
          ]},
        ],
      },
      {
        label: "ERP Software",
        desc: "End-to-end resource planning for any business.",
        icon: Building2,
        groups: [
          { title: "Modules", items: [
            { label: "Inventory & Warehouse", to: "/services/erp-solution/inventory-warehouse", icon: Boxes, desc: "Explore inventory & warehouse" },
            { label: "Sales & CRM", to: "/services/erp-solution/sales-purchase", icon: Briefcase, desc: "Explore sales & crm" },
            { label: "Procurement", to: "/services/erp-solution/sales-purchase", icon: ShoppingBag, desc: "Explore procurement" },
            { label: "Manufacturing", to: "/services/erp-solution/manufacturing", icon: Building, desc: "Explore manufacturing" },
            { label: "Supply Chain", to: "/services/erp-solution/supply-chain", icon: Truck, desc: "Explore supply chain" },
          ]},
          { title: "Finance", items: [
            { label: "Accounting & Invoicing", to: "/services/erp-solution/accounting-invoicing", icon: DollarSign, desc: "Explore accounting & invoicing" },
            { label: "Reporting & Analytics", to: "/services/erp-solution/reporting-analytics", icon: BarChart3, desc: "Explore reporting & analytics" },
            { label: "Payroll", to: "/services/erp-solution/payroll", icon: CreditCard, desc: "Explore payroll" },
          ]},
          { title: "Customization", items: [
            { label: "Workflow Automation", to: "/services/erp-solution/workflow-automation", icon: Zap, desc: "Explore workflow automation" },
            { label: "Role-based Permissions", to: "/services/erp-solution/role-based-permissions", icon: Lock, desc: "Explore role-based permissions" },
            { label: "Data Migration & Setup", to: "/services/erp-solution/data-migration-setup", icon: Database, desc: "Explore data migration & setup" },
            { label: "Training & Support", to: "/services/erp-solution/training-support", icon: Headphones, desc: "Explore training & support" },
          ]},
        ],
      },
      {
        label: "POS Software",
        desc: "Retail & restaurant point of sale.",
        icon: CreditCard,
        groups: [
          { title: "Retail POS", items: [
            { label: "Single-store POS", to: "/services/pos-software/single-store-pos", icon: ShoppingBag, desc: "Explore single-store pos" },
            { label: "Multi-branch POS", to: "/services/pos-software/multi-branch-pos", icon: Building, desc: "Explore multi-branch pos" },
            { label: "Barcode & Inventory", to: "/services/pos-software/barcode-inventory", icon: Boxes, desc: "Explore barcode & inventory" },
          ]},
          { title: "Restaurant POS", items: [
            { label: "Table Management", to: "/services/pos-software/table-management", icon: Layout, desc: "Explore table management" },
            { label: "Kitchen Display", to: "/services/pos-software/kitchen-display", icon: Headphones, desc: "Explore kitchen display" },
            { label: "Online Orders", to: "/services/pos-software/online-orders", icon: Globe, desc: "Explore online orders" },
          ]},
        ],
      },
      {
        label: "HRM & Payroll",
        desc: "People operations, hiring to payroll.",
        icon: Users,
        groups: [
          { title: "HR", items: [
            { label: "Employee Records", to: "/services/hrm-payroll/employee-records", icon: UserCheck, desc: "Explore employee records" },
            { label: "Attendance & Leave", to: "/services/hrm-payroll/attendance-leave", icon: FileText, desc: "Explore attendance & leave" },
            { label: "Recruitment", to: "/services/hrm-payroll/recruitment", icon: Briefcase, desc: "Explore recruitment" },
          ]},
          { title: "Payroll", items: [
            { label: "Payroll Processing", to: "/services/hrm-payroll/payroll-processing", icon: DollarSign, desc: "Explore payroll processing" },
            { label: "Tax Compliance", to: "/services/hrm-payroll/tax-compliance", icon: ShieldCheck, desc: "Explore tax compliance" },
          ]},
        ],
      },
      {
        label: "Industry Solutions",
        desc: "Pre-built systems for vertical industries.",
        icon: Layers,
        groups: [
          { title: "Education", items: [
            { label: "School Management", to: "/services/industry-solutions/school-management", icon: GraduationCap, desc: "Explore school management" },
            { label: "LMS Platform", to: "/services/industry-solutions/lms-platform", icon: BookOpen, desc: "Explore lms platform" },
          ]},
          { title: "Healthcare", items: [
            { label: "Hospital Management", to: "/services/industry-solutions/hospital-management", icon: Stethoscope, desc: "Explore hospital management" },
            { label: "Clinic & Pharmacy", to: "/services/industry-solutions/clinic-pharmacy", icon: ShieldCheck, desc: "Explore clinic & pharmacy" },
          ]},
          { title: "Logistics", items: [
            { label: "Fleet Management", to: "/services/industry-solutions/fleet-management", icon: Truck, desc: "Explore fleet management" },
            { label: "Warehouse Management", to: "/services/industry-solutions/warehouse-management", icon: Package, desc: "Explore warehouse management" },
          ]},
        ],
      },
    ],
    featured: { title: "Need a custom build?", desc: "Talk to our solution architects today.", to: "/contact" },
  },
  {
    label: "Marketing",
    to: "/marketing",
    categories: [
      {
        label: "Search & SEO",
        to: "/marketing/search-seo",
        desc: "Win organic traffic that compounds over time.",
        icon: Search,
        groups: [
          { title: "SEO", items: [
            { label: "Technical SEO", to: "/marketing/search-seo/technical-seo", icon: Cpu, desc: "Core Web Vitals, crawl & schema fixes" },
            { label: "On-page SEO", to: "/marketing/search-seo/on-page-seo", icon: FileText, desc: "Titles, headings & content optimization" },
            { label: "Link Building", to: "/marketing/search-seo/link-building", icon: Network, desc: "Authoritative backlinks that rank" },
          ]},
          { title: "Content", items: [
            { label: "Keyword Research", to: "/marketing/search-seo/keyword-research", icon: Search, desc: "Intent-mapped keyword strategy" },
            { label: "Blog & Articles", to: "/marketing/search-seo/blog-articles", icon: FileText, desc: "Pillar pages & topic clusters" },
            { label: "AI Content", to: "/marketing/search-seo/ai-content", icon: Sparkles, desc: "Scaled content with human editing" },
          ]},
        ],
      },
      {
        label: "Paid Advertising",
        to: "/marketing/paid-advertising",
        desc: "Performance ads across every major channel.",
        icon: BarChart3,
        groups: [
          { title: "Channels", items: [
            { label: "Google Ads", to: "/marketing/paid-advertising/google-ads", icon: Search, desc: "Search, Shopping & Performance Max" },
            { label: "Meta Ads", to: "/marketing/paid-advertising/meta-ads", icon: Mega, desc: "Facebook & Instagram advertising" },
            { label: "LinkedIn Ads", to: "/marketing/paid-advertising/linkedin-ads", icon: Briefcase, desc: "B2B targeting that actually converts" },
            { label: "TikTok Ads", to: "/marketing/paid-advertising/tiktok-ads", icon: Video, desc: "Short-form video ads at scale" },
          ]},
          { title: "Optimization", items: [
            { label: "Landing Pages", to: "/marketing/paid-advertising/landing-pages", icon: Layout, desc: "High-converting ad landing pages" },
            { label: "A/B Testing", to: "/marketing/paid-advertising/ab-testing", icon: Gauge, desc: "Creative & offer experimentation" },
            { label: "Tracking & Pixels", to: "/marketing/paid-advertising/tracking-pixels", icon: Target, desc: "GA4, GTM & server-side tracking" },
          ]},
        ],
      },
      {
        label: "Social & Content",
        to: "/marketing/social-content",
        desc: "Build a brand people actually care about.",
        icon: Share2,
        groups: [
          { title: "Social", items: [
            { label: "Social Strategy", to: "/marketing/social-content/social-strategy", icon: Target, desc: "Channel strategy & content pillars" },
            { label: "Community Management", to: "/marketing/social-content/community-management", icon: Users, desc: "Engagement, DMs & moderation" },
            { label: "Influencer Marketing", to: "/marketing/social-content/influencer-marketing", icon: Award, desc: "Creator partnerships that convert" },
          ]},
          { title: "Creative", items: [
            { label: "Video Production", to: "/marketing/social-content/video-production", icon: Video, desc: "Shorts, reels & long-form video" },
            { label: "Graphic Design", to: "/marketing/social-content/graphic-design", icon: Brush, desc: "On-brand social & ad creative" },
            { label: "Brand Storytelling", to: "/marketing/social-content/brand-storytelling", icon: Sparkles, desc: "Narrative that earns attention" },
          ]},
        ],
      },
      {
        label: "Email & Automation",
        to: "/marketing/email-automation",
        desc: "Lifecycle campaigns that nurture and convert.",
        icon: Mail,
        groups: [
          { title: "Email", items: [
            { label: "Email Campaigns", to: "/marketing/email-automation/email-campaigns", icon: Send, desc: "Broadcast & lifecycle campaigns" },
            { label: "Newsletter Strategy", to: "/marketing/email-automation/newsletter-strategy", icon: FileText, desc: "Owned audience growth engine" },
            { label: "Transactional Email", to: "/marketing/email-automation/transactional-email", icon: Mail, desc: "Reliable transactional delivery" },
          ]},
          { title: "Automation", items: [
            { label: "Marketing Automation", to: "/marketing/email-automation/marketing-automation", icon: Bot, desc: "Journeys, triggers & workflows" },
            { label: "CRM Integration", to: "/marketing/email-automation/crm-integration", icon: Briefcase, desc: "HubSpot, Salesforce & more" },
            { label: "Lead Scoring", to: "/marketing/email-automation/lead-scoring", icon: TrendingUp, desc: "Prioritize sales-ready leads" },
          ]},
        ],
      },
      {
        label: "Local SEO",
        to: "/marketing/local-seo",
        desc: "Dominate local search and map results.",
        icon: MapPin,
        groups: [
          { title: "Google Business", items: [
            { label: "GMB Setup", to: "/marketing/local-seo/gmb-setup", icon: Store, desc: "Explore gmb setup" },
            { label: "GMB Verification", to: "/marketing/local-seo/gmb-verification", icon: CheckCircle, desc: "Explore gmb verification" },
            { label: "GMB Optimization", to: "/marketing/local-seo/gmb-optimization", icon: Gauge, desc: "Explore gmb optimization" },
            { label: "GMB Ranking", to: "/marketing/local-seo/gmb-ranking", icon: TrendingUp, desc: "Explore gmb ranking" },
            { label: "GMB Reinstatement", to: "/marketing/local-seo/gmb-reinstatement", icon: ShieldCheck, desc: "Explore gmb reinstatement" },
          ]},
          { title: "Local Presence", items: [
            { label: "Google Maps SEO", to: "/marketing/local-seo/google-maps-seo", icon: MapPin, desc: "Explore google maps seo" },
            { label: "Citation Building", to: "/marketing/local-seo/citation-building", icon: Building, desc: "Explore citation building" },
            { label: "Local Listings", to: "/marketing/local-seo/local-listings", icon: ClipboardList, desc: "Explore local listings" },
            { label: "Map Pack Ranking", to: "/marketing/local-seo/map-pack-ranking", icon: MapPin, desc: "Explore map pack ranking" },
          ]},
          { title: "Reputation", items: [
            { label: "Google Reviews", to: "/marketing/local-seo/google-reviews", icon: Star, desc: "Explore google reviews" },
            { label: "Reputation Management", to: "/marketing/local-seo/reputation-management", icon: ShieldCheck, desc: "Explore reputation management" },
          ]},
        ],
      },
      {
        label: "Amazon Services",
        to: "/marketing/amazon",
        desc: "End-to-end Amazon marketplace solutions.",
        icon: Store,
        groups: [
          { title: "Account", items: [
            { label: "Amazon Seller Account", to: "/marketing/amazon/amazon-seller-account", icon: Store, desc: "Explore amazon seller account" },
            { label: "Amazon Approval", to: "/marketing/amazon/amazon-approval", icon: CheckCircle, desc: "Explore amazon approval" },
            { label: "Amazon Verification", to: "/marketing/amazon/amazon-verification", icon: ShieldCheck, desc: "Explore amazon verification" },
            { label: "Account Reinstatement", to: "/marketing/amazon/account-reinstatement", icon: Landmark, desc: "Explore account reinstatement" },
          ]},
          { title: "Product & SEO", items: [
            { label: "Amazon SEO", to: "/marketing/amazon/amazon-seo", icon: Search, desc: "Explore amazon seo" },
            { label: "Product Listing", to: "/marketing/amazon/product-listing", icon: Package, desc: "Explore product listing" },
            { label: "Keyword Research", to: "/marketing/amazon/keyword-research", icon: Search, desc: "Explore keyword research" },
            { label: "Keyword Ranking", to: "/marketing/amazon/keyword-ranking", icon: TrendingUp, desc: "Explore keyword ranking" },
            { label: "Product Reviews", to: "/marketing/amazon/product-reviews", icon: Star, desc: "Explore product reviews" },
          ]},
          { title: "Ads & Logistics", items: [
            { label: "Amazon PPC", to: "/marketing/amazon/amazon-ppc", icon: BarChart3, desc: "Explore amazon ppc" },
            { label: "FBA Setup", to: "/marketing/amazon/fba-setup", icon: Boxes, desc: "Explore fba setup" },
            { label: "Brand Registry", to: "/marketing/amazon/brand-registry", icon: Award, desc: "Explore brand registry" },
          ]},
        ],
      },
      {
        label: "Lead Generation",
        to: "/marketing/lead-generation",
        desc: "High-intent leads delivered to your pipeline.",
        icon: Users,
        groups: [
          { title: "Lead Types", items: [
            { label: "B2B Leads", to: "/marketing/lead-generation/b2b-leads", icon: Briefcase, desc: "Explore b2b leads" },
            { label: "B2C Leads", to: "/marketing/lead-generation/b2c-leads", icon: Users, desc: "Explore b2c leads" },
            { label: "Sales Leads", to: "/marketing/lead-generation/sales-leads", icon: DollarSign, desc: "Explore sales leads" },
            { label: "Targeted Leads", to: "/marketing/lead-generation/targeted-leads", icon: Target, desc: "Explore targeted leads" },
            { label: "Email Leads", to: "/marketing/lead-generation/email-leads", icon: Mail, desc: "Explore email leads" },
          ]},
          { title: "Outbound", items: [
            { label: "Cold Calling", to: "/marketing/lead-generation/cold-calling", icon: Phone, desc: "Outbound call selling & cold calling" },
            { label: "Appointment Setting", to: "/marketing/lead-generation/appointment-setting", icon: CalendarDays, desc: "Explore appointment setting" },
            { label: "LinkedIn Leads", to: "/marketing/lead-generation/linkedin-leads", icon: AtSign, desc: "Explore linkedin leads" },
            { label: "Facebook Leads", to: "/marketing/lead-generation/facebook-leads", icon: Share2, desc: "Explore facebook leads" },
          ]},
          { title: "Channels & CRM", items: [
            { label: "WhatsApp Marketing", to: "/marketing/lead-generation/whatsapp-marketing", icon: MessageCircle, desc: "Explore whatsapp marketing" },
            { label: "SMS Marketing", to: "/marketing/lead-generation/sms-marketing", icon: Phone, desc: "Explore sms marketing" },
            { label: "CRM Management", to: "/marketing/lead-generation/crm-management", icon: Briefcase, desc: "Explore crm management" },
            { label: "Funnel Marketing", to: "/marketing/lead-generation/funnel-marketing", icon: Funnel, desc: "Explore funnel marketing" },
            { label: "Landing Page Leads", to: "/marketing/lead-generation/landing-page-leads", icon: Layout, desc: "Explore landing page leads" },
          ]},
        ],
      },
    ],
    featured: { title: "Get a free growth audit", desc: "We'll map your funnel and find quick wins.", to: "/contact" },
  },
  {
    label: "Domain & Hosting",
    to: "/hosting",
    categories: [
      { label: "Web Hosting", to: "/hosting/web", desc: "Fast, secure hosting for any website.", icon: Cloud, groups: [] },
      { label: "VPS & Cloud", to: "/hosting/vps-cloud", desc: "Scalable performance for production workloads.", icon: Server, groups: [] },
      { label: "Dedicated Servers", to: "/hosting/dedicated-servers", desc: "Raw power, total control.", icon: HardDrive, groups: [] },
      { label: "Domains", to: "/hosting/domains", desc: "Search, register, and manage domains.", icon: Globe, groups: [] },
      { label: "Domain Value Checker", to: "/domain-value", desc: "Instant AI-powered domain price estimate.", icon: TrendingUp, groups: [] },
      { label: "Billing & WHMCS", to: "/whmcs", desc: "Client portal for invoices, domains and hosting.", icon: CreditCard, groups: [] },
    ],
  },

  {
    label: "Company",
    categories: [
      {
        label: "About CoderLity",
        desc: "Who we are and what we stand for.",
        icon: Building2,
        groups: [
          { title: "Company", items: [
            { label: "About Us", to: "/about", icon: Building2, desc: "Our story, mission & vision" },
            { label: "Our Team", to: "/about", icon: Users, desc: "Meet the people behind the work" },
            { label: "Branches & Offices", to: "/about", icon: MapPin, desc: "Where you can find us" },
            { label: "Awards & Recognition", to: "/about", icon: Award, desc: "Milestones and honors" },
          ]},
          { title: "Careers", items: [
            { label: "Open Positions", to: "/about", icon: Briefcase, desc: "Join our growing team" },
            { label: "Life at CoderLity", to: "/about", icon: Sparkles, desc: "Culture, perks & values" },
            { label: "Internships", to: "/about", icon: GraduationCap, desc: "Kickstart your career" },
          ]},
          { title: "Press", items: [
            { label: "Newsroom", to: "/blog", icon: FileText, desc: "Latest announcements" },
            { label: "Media Kit", to: "/about", icon: ShoppingBag, desc: "Brand assets & logos" },
          ]},
        ],
      },
      {
        label: "Portfolio & Cases",
        desc: "Selected work and client success stories.",
        icon: ShoppingBag,
        groups: [
          { title: "Work", items: [
            { label: "Featured Projects", to: "/portfolio", icon: Award, desc: "Signature builds" },
            { label: "Case Studies", to: "/portfolio", icon: FileText, desc: "In-depth results" },
            { label: "Client Testimonials", to: "/testimonials", icon: MessageCircle, desc: "What clients say" },
          ]},
          { title: "Industries", items: [
            { label: "E-commerce", to: "/portfolio", icon: ShoppingBag, desc: "Storefronts & marketplaces" },
            { label: "Healthcare", to: "/portfolio", icon: Stethoscope, desc: "Clinics, hospitals & labs" },
            { label: "Fintech", to: "/portfolio", icon: DollarSign, desc: "Banking & payments" },
            { label: "Education", to: "/portfolio", icon: GraduationCap, desc: "Schools & LMS" },
          ]},
        ],
      },
      {
        label: "Resources",
        desc: "Insights, guides, and tutorials.",
        icon: BookOpen,
        groups: [
          { title: "Learn", items: [
            { label: "Blog", to: "/blog", icon: FileText, desc: "Articles & product updates" },
            { label: "Guides", to: "/blog", icon: BookOpen, desc: "Long-form how-tos" },
            { label: "Tutorials", to: "/blog", icon: Video, desc: "Step-by-step videos" },
          ]},
          { title: "Reference", items: [
            { label: "Knowledge Base", to: "/kb", icon: BookOpen, desc: "Docs & help articles" },
            { label: "FAQ", to: "/faq", icon: MessageCircle, desc: "Quick answers" },
            { label: "Industry Reports", to: "/blog", icon: BarChart3, desc: "Market insights" },
          ]},
        ],
      },
      {
        label: "Contact & Support",
        desc: "Talk to sales, support, or partnerships.",
        icon: LifeBuoy,
        groups: [
          { title: "Get in touch", items: [
            { label: "Contact Us", to: "/contact", icon: Mail, desc: "General inquiries" },
            { label: "Contact Sales", to: "/contact", icon: MessageCircle, desc: "Talk to our sales team" },
            { label: "Partnerships", to: "/contact", icon: Briefcase, desc: "Grow with CoderLity" },
            { label: "Call Us", to: "/contact", icon: Phone, desc: "Speak to a human" },
          ]},
          { title: "Support", items: [
            { label: "Support Center", to: "/support", icon: LifeBuoy, desc: "Get help fast" },
            { label: "Submit a Ticket", to: "/support", icon: Send, desc: "Open a support request" },
            { label: "System Status", to: "/support", icon: Gauge, desc: "Live uptime dashboard" },
          ]},
          { title: "Community", items: [
            { label: "Testimonials", to: "/testimonials", icon: Star, desc: "Reviews from customers" },
            { label: "Book a Meeting", to: "/contact", icon: CalendarDays, desc: "Schedule a call" },
          ]},
        ],
      },
      {
        label: "Legal & Trust",
        desc: "Policies, compliance and service commitments.",
        icon: ShieldCheck,
        groups: [
          { title: "Policies", items: [
            { label: "Terms of Service", to: "/terms", icon: FileText, desc: "Rules for using our services" },
            { label: "Privacy Policy", to: "/privacy", icon: Lock, desc: "How we handle your data" },
            { label: "Cookie Policy", to: "/cookies", icon: FileText, desc: "Cookies & tracking" },
            { label: "Refund Policy", to: "/refund", icon: DollarSign, desc: "Refunds & cancellations" },
          ]},
          { title: "Agreements", items: [
            { label: "Service Level Agreement", to: "/sla", icon: CheckCircle, desc: "Uptime & response SLAs" },
            { label: "Acceptable Use", to: "/terms", icon: ShieldCheck, desc: "What is and isn't allowed" },
            { label: "DPA / GDPR", to: "/privacy", icon: Lock, desc: "Data processing addendum" },
          ]},
          { title: "Trust", items: [
            { label: "Security", to: "/privacy", icon: ShieldCheck, desc: "How we protect you" },
            { label: "Compliance", to: "/privacy", icon: Award, desc: "Certifications & audits" },
          ]},
        ],
      },
    ],
    featured: { title: "Contact our team", desc: "We usually reply within a few hours.", to: "/contact" },
  },

];

function MegaPanel({ menu, onNavigate }: { menu: Menu; onNavigate: () => void }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Highlight the category whose `to` matches the current URL (deepest match wins).
  const routeMatchIdx = useMemo(() => {
    let best = -1;
    let bestLen = 0;
    menu.categories.forEach((c, i) => {
      if (c.to && (pathname === c.to || pathname.startsWith(c.to + "/"))) {
        if (c.to.length > bestLen) {
          bestLen = c.to.length;
          best = i;
        }
      }
    });
    return best;
  }, [menu, pathname]);
  const [activeIdx, setActiveIdx] = useState(() => (routeMatchIdx >= 0 ? routeMatchIdx : 0));
  // Keep tab in sync when route changes while menu is open.
  useEffect(() => {
    if (routeMatchIdx >= 0) setActiveIdx(routeMatchIdx);
  }, [routeMatchIdx]);
  const active = menu.categories[activeIdx] ?? menu.categories[0];
  const hasDetail = menu.categories.some((c) => c.groups && c.groups.length > 0);

  return (
    <div className={cn("flex gap-3", hasDetail ? "items-stretch" : "")}>
      {/* Left categories */}
      <div className={hasDetail ? "w-64 shrink-0" : "w-full"}>
        <div className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {menu.label}
        </div>
        <ul role="tablist" aria-orientation="vertical">
          {menu.categories.map((c, idx) => {
            const isActive = idx === activeIdx;
            const isCurrent = idx === routeMatchIdx;
            const onKey = (e: React.KeyboardEvent) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveIdx((idx + 1) % menu.categories.length);
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveIdx((idx - 1 + menu.categories.length) % menu.categories.length);
              }
            };
            const inner = (
              <>
                <c.icon className={cn("mt-0.5 h-4 w-4 shrink-0", isActive || isCurrent ? "text-primary" : "text-muted-foreground")} />
                <span className="flex-1 min-w-0">
                  <span className="block truncate text-sm font-semibold text-foreground">
                    {c.label}
                    {isCurrent && <span className="sr-only"> (current page)</span>}
                  </span>
                  {c.desc && (
                    <span className="mt-0.5 block line-clamp-2 text-[11px] leading-snug text-muted-foreground">
                      {c.desc}
                    </span>
                  )}
                </span>
                <ChevronRight
                  className={cn(
                    "mt-1 h-3.5 w-3.5 shrink-0 transition",
                    isActive || isCurrent ? "text-primary" : "text-muted-foreground/50"
                  )}
                />
              </>
            );
            const rowClass = cn(
              "group flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left transition border border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isActive ? "bg-secondary" : "hover:bg-secondary/60",
              isCurrent && "border-primary/40 bg-primary/5"
            );
            return (
              <li key={c.label} role="presentation">
                {c.to ? (
                  <Link
                    to={c.to}
                    role="tab"
                    aria-selected={isActive}
                    aria-current={isCurrent ? "page" : undefined}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onFocus={() => setActiveIdx(idx)}
                    onKeyDown={onKey}
                    onClick={onNavigate}
                    className={rowClass}
                  >
                    {inner}
                  </Link>
                ) : (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onFocus={() => setActiveIdx(idx)}
                    onKeyDown={onKey}
                    className={rowClass}
                  >
                    {inner}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>


      {/* Right: detail */}
      {hasDetail && (
      <div className="flex-1 rounded-lg border border-border bg-card p-3">
        <AnimatePresence mode="wait">
          <motion.div
            key={active.label}
            initial={{ opacity: 0, x: 6 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -6 }}
            transition={{ duration: 0.15 }}
          >
            <div className="mb-2 flex items-center justify-between gap-4">
              {active.to ? (
                <Link to={active.to} onClick={onNavigate} className="text-sm font-bold text-foreground hover:text-primary transition inline-flex items-center gap-1">
                  {active.label} <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              ) : (
                <h3 className="text-sm font-bold text-foreground">{active.label}</h3>
              )}
              {menu.featured && (
                <Link
                  to={menu.featured.to}
                  onClick={onNavigate}
                  className="hidden md:inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-semibold text-foreground hover:bg-secondary transition"
                >
                  {menu.featured.title}
                </Link>
              )}
            </div>

            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {active.groups.map((g) => (
                <div key={g.title} className="min-w-[180px] flex-1 basis-[180px] max-w-[240px]">
                  <div className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {g.title}
                  </div>
                  <ul>
                    {g.items.map((it) => {
                      const itemActive = pathname === it.to;
                      return (
                      <li key={it.label}>
                        <Link
                          to={it.to}
                          onClick={onNavigate}
                          aria-current={itemActive ? "page" : undefined}
                          className={cn(
                            "flex items-start gap-2 rounded-md px-2 py-1 text-sm font-medium text-foreground transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                            itemActive ? "bg-primary/10 text-primary" : "hover:bg-secondary"
                          )}
                        >
                          <it.icon className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", itemActive ? "text-primary" : "text-muted-foreground")} />
                          <span className="flex-1 min-w-0">
                            <span className="block truncate">{it.label}</span>
                            {it.desc && (
                              <span className="mt-0.5 block line-clamp-1 text-[11px] leading-snug text-muted-foreground">
                                {it.desc}
                              </span>
                            )}
                          </span>
                        </Link>
                      </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      )}
    </div>
  );
}

function mergeDbMenu(base: Menu[], key: string, cfg: MegaMenuConfig | null | undefined): Menu[] {
  if (!cfg || !Array.isArray(cfg.categories)) return base;
  const label = cfg.label ?? key.charAt(0).toUpperCase() + key.slice(1);
  const dynamic: Menu = {
    label,
    featured: cfg.featured,
    categories: cfg.categories.map((c) => ({
      label: c.label,
      desc: c.desc ?? "",
      icon: resolveMegaMenuIcon(c.icon),
      groups: (c.groups ?? []).map((g) => ({
        title: g.title,
        items: (g.items ?? []).map((it) => ({
          label: it.label,
          to: it.to,
          icon: resolveMegaMenuIcon(it.icon),
          desc: it.desc,
        })),
      })),
    })),
  };
  const idx = base.findIndex((m) => m.label.toLowerCase() === label.toLowerCase());
  if (idx === -1) return [...base, dynamic];
  const next = [...base];
  next[idx] = dynamic;
  return next;
}

export function Header() {
  const [open, setOpen] = useState<string | null>(null);
  const [mobile, setMobile] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guard against double-fired click handlers (rapid double-clicks, React
  // StrictMode re-renders, or Link + parent onClick both toggling state).
  const lastNavAt = useRef(0);
  const handleNavigate = useCallback(() => {
    const now = Date.now();
    if (now - lastNavAt.current < 250) return;
    lastNavAt.current = now;
    setOpen(null);
    setMobile(false);
  }, []);
  const { data: servicesCfg } = useMegaMenu("services");
  const { data: marketingCfg } = useMegaMenu("marketing");
  const { data: softwareCfg } = useMegaMenu("software");
  const { data: hostingCfg } = useMegaMenu("hosting");
  const { data: companyCfg } = useMegaMenu("company");

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(null), 160);
  };
  const openNow = (label: string) => {
    cancelClose();
    setOpen(label);
  };
  useEffect(() => () => cancelClose(), []);

  const liveMenus = useMemo(() => {
    let next = menus;
    next = mergeDbMenu(next, "services", servicesCfg);
    next = mergeDbMenu(next, "marketing", marketingCfg);
    next = mergeDbMenu(next, "software", softwareCfg);
    next = mergeDbMenu(next, "hosting", hostingCfg);
    next = mergeDbMenu(next, "company", companyCfg);
    return next;
  }, [servicesCfg, marketingCfg, softwareCfg, hostingCfg, companyCfg]);

  const openMenu = useMemo(() => liveMenus.find((m) => m.label === open), [liveMenus, open]);

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setOpen(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(null);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);


  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="absolute inset-0 -z-10 bg-background/95 backdrop-blur-md border-b border-border shadow-sm supports-[backdrop-filter]:bg-background/80" />
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 lg:px-8">
        <div className="flex items-center gap-8">
          <Logo />
          <nav ref={navRef} className="hidden lg:flex items-center gap-1" aria-label="Primary">
            <Link
              to="/"
              onMouseEnter={() => { cancelClose(); setOpen(null); }}
              activeOptions={{ exact: true }}
              activeProps={{ className: "text-foreground" }}
              className="inline-flex min-h-[44px] items-center rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              Home
            </Link>
            {liveMenus.map((m) => {
              const isOpen = open === m.label;
              const panelId = `mega-panel-${m.label.toLowerCase().replace(/\s+/g, "-")}`;
              return (
                <div
                  key={m.label}
                  onMouseEnter={() => openNow(m.label)}
                  onMouseLeave={scheduleClose}
                  className="relative"
                >
                  {m.to ? (
                    <Link
                      to={m.to}
                      aria-haspopup="true"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={handleNavigate}
                      onFocus={() => openNow(m.label)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") setOpen(null);
                        if (e.key === "ArrowDown" && !isOpen) { e.preventDefault(); openNow(m.label); }
                      }}
                      className={cn(
                        "flex min-h-[44px] items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        isOpen && "text-foreground"
                      )}
                      activeProps={{ className: "text-foreground" }}
                    >
                      {m.label}
                      <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", isOpen && "rotate-180")} />
                    </Link>
                  ) : (
                    <button
                      type="button"
                      aria-haspopup="true"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => openNow(m.label)}
                      onFocus={() => openNow(m.label)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") setOpen(null);
                        if (e.key === "ArrowDown" && !isOpen) { e.preventDefault(); openNow(m.label); }
                      }}
                      className={cn(
                        "flex min-h-[44px] items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        isOpen && "text-foreground"
                      )}
                    >
                      {m.label}
                      <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", isOpen && "rotate-180")} />
                    </button>
                  )}
                </div>
              );
            })}
            <Link
              to="/marketplace"
              onMouseEnter={() => { cancelClose(); setOpen(null); }}
              className="inline-flex min-h-[44px] items-center rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
              activeProps={{ className: "text-foreground" }}
            >
              Marketplace
            </Link>
          </nav>

        </div>

        <div className="flex items-center gap-2">
          <HeaderUtilities />
          <button className="lg:hidden rounded-md min-h-[44px] min-w-[44px] flex items-center justify-center p-2 text-foreground" onClick={() => setMobile((v) => !v)} aria-label="Menu">
            {mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mega menu panel */}
      <AnimatePresence>
        {openMenu && (
          <motion.div
            key={openMenu.label}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.16 }}
            className="absolute inset-x-0 top-full hidden lg:block"
            onMouseEnter={() => openNow(openMenu.label)}
            onMouseLeave={scheduleClose}
          >
            {/* Invisible hover bridge so cursor can travel from trigger to panel without dropping */}
            <div aria-hidden className="h-3 w-full" />
            {(() => {
              const compact = !openMenu.categories.some((c) => c.groups && c.groups.length > 0);
              return (
                <div className={cn("mx-auto w-fit px-4 lg:px-8", compact ? "max-w-md" : "max-w-4xl")}>
                  <div
                    id={`mega-panel-${openMenu.label.toLowerCase().replace(/\s+/g, "-")}`}
                    role="region"
                    aria-label={`${openMenu.label} menu`}
                    className="max-h-[calc(100vh-6rem)] overflow-auto rounded-2xl border border-border bg-popover text-popover-foreground p-3 shadow-card"
                  >
                    <MegaPanel menu={openMenu} onNavigate={handleNavigate} />
                  </div>
                </div>
              );
            })()}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile */}
      <AnimatePresence>
        {mobile && (
          <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="lg:hidden overflow-hidden border-b border-border bg-background">
            <div className="px-4 py-4 space-y-1 max-h-[80vh] overflow-y-auto">
              <Link to="/" onClick={handleNavigate} className="block rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium hover:bg-secondary transition">
                Home
              </Link>
              {liveMenus.map((m) => (
                <details key={m.label} className="group rounded-lg border border-border bg-card">
                  <summary className="flex cursor-pointer items-center justify-between px-3 py-2 text-sm font-medium">
                    <span className="inline-flex items-center gap-2">
                      {m.label}
                      {m.to && (
                        <Link
                          to={m.to}
                          onClick={handleNavigate}
                          className="ml-1 text-[10px] uppercase tracking-wider text-primary hover:underline"
                        >
                          Overview →
                        </Link>
                      )}
                    </span>
                    <ChevronDown className="h-4 w-4 transition group-open:rotate-180" />
                  </summary>
                  <div className="px-3 pb-3 space-y-3">
                    {m.categories.map((c) => (
                      <details key={c.label} className="rounded-md border border-border/60 bg-background/50">
                        <summary className="flex cursor-pointer items-center justify-between gap-2 px-2.5 py-2 text-xs font-semibold text-foreground">
                          <span className="inline-flex min-w-0 items-center gap-2">
                            <c.icon className="h-3.5 w-3.5 shrink-0 text-primary" />
                            <span className="truncate">{c.label}</span>
                          </span>
                          {c.to && (
                            <Link
                              to={c.to}
                              onClick={handleNavigate}
                              className="ml-2 text-[10px] uppercase tracking-wider text-primary hover:underline"
                            >
                              Open
                            </Link>
                          )}
                        </summary>
                        <div className="px-2.5 pb-2 grid grid-cols-2 gap-1">
                          {c.groups.flatMap((g) => g.items).map((it) => (
                            <Link key={it.label} to={it.to} onClick={handleNavigate} activeProps={{ className: "text-primary bg-primary/10" }} className="flex min-h-11 items-center rounded-md px-2 text-xs text-muted-foreground hover:text-foreground hover:bg-secondary transition">
                              {it.label}
                            </Link>
                          ))}
                        </div>
                      </details>
                    ))}
                  </div>
                </details>
              ))}
              <Link to="/marketplace" onClick={handleNavigate} className="block rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium hover:bg-secondary transition">
                Marketplace
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
